import { ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import type { AdminDashboardCardConfig } from '../adminDashboardConfig';

const iconWrapClassName = 'bg-stone-50 text-stone-600 ring-1 ring-stone-100';

type GroupItemProps = {
	config: AdminDashboardCardConfig;
	stats: { primary: string; secondary: string[] } | null;
	showDivider: boolean;
};

const GroupItem = ({ config, stats, showDivider }: GroupItemProps) => {
	const navigate = useNavigate();
	const Icon = config.icon;
	const hasStats = stats != null && stats.primary.length > 0;

	return (
		<>
			{showDivider ? (
				<div className="mx-1 border-t border-stone-100" aria-hidden />
			) : null}
			<button
				type="button"
				onClick={() => navigate(config.path)}
				className="group/item flex w-full items-center gap-2.5 rounded-xl px-2 py-2.5 text-left transition-colors hover:bg-stone-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-300"
			>
				<div
					className={cn(
						'flex size-7 shrink-0 items-center justify-center rounded-lg',
						iconWrapClassName,
					)}
				>
					<Icon className="size-3.5" strokeWidth={2.25} aria-hidden />
				</div>

				<div className="min-w-0 flex-1">
					<h3 className="font-albert truncate text-[11px] font-bold text-stone-900">
						{config.title}
					</h3>
					{hasStats ? (
						<p className="font-albert mt-0.5 text-sm font-black tabular-nums leading-none text-stone-500">
							{stats.primary}
						</p>
					) : null}
				</div>

				<ChevronRight
					className="size-3 shrink-0 text-stone-300 transition-colors group-hover/item:text-stone-500"
					aria-hidden
				/>
			</button>
		</>
	);
};

type AdminDashboardGroupCardProps = {
	keys: string[];
	indicators: Dashboard.Indicators;
	getCard: (key: string) => AdminDashboardCardConfig | undefined;
};

export const AdminDashboardGroupCard = ({
	keys,
	indicators,
	getCard,
}: AdminDashboardGroupCardProps) => {
	const items = keys
		.map((key) => {
			const config = getCard(key);
			if (!config) return null;
			return {
				config,
				stats: config.getStats?.(indicators) ?? null,
			};
		})
		.filter((item): item is NonNullable<typeof item> => item != null);

	if (items.length === 0) return null;

	return (
		<article className="flex flex-col justify-center rounded-[1.25rem] border border-stone-100 bg-white p-2 shadow-sm">
			{items.map((item, index) => (
				<GroupItem
					key={item.config.key}
					config={item.config}
					stats={item.stats}
					showDivider={index > 0}
				/>
			))}
		</article>
	);
};
